import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Coffee } from "lucide-react";
import { Reveal, Overline, MaskLine, EASE } from "@/components/Motion";
import { MENU_CATEGORIES } from "@/data/menu";

const VegMark = ({ nv }) => (
  <span
    className={`mt-1 inline-flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-[3px] border ${
      nv ? "border-red-700" : "border-green-700"
    }`}
    aria-label={nv ? "Non-vegetarian" : "Vegetarian"}
    title={nv ? "Non-vegetarian" : "Vegetarian"}
  >
    <span className={`h-[7px] w-[7px] rounded-full ${nv ? "bg-red-700" : "bg-green-700"}`} />
  </span>
);

export default function MenuPage() {
  const [searchParams] = useSearchParams();
  const requested = searchParams.get("cat");
  const [active, setActive] = useState(
    MENU_CATEGORIES.some((c) => c.id === requested) ? requested : MENU_CATEGORIES[0].id
  );

  useEffect(() => {
    if (requested && MENU_CATEGORIES.some((c) => c.id === requested)) setActive(requested);
  }, [requested]);
  const category = MENU_CATEGORIES.find((c) => c.id === active);

  return (
    <main className="pt-[72px]" data-testid="menu-page">
      <section className="bg-wine text-cream py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <Overline className="text-brass">La Carta</Overline>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 max-w-4xl" data-testid="menu-page-title">
            <MaskLine>A Menu of</MaskLine>
            <MaskLine delay={0.15}>
              <span className="italic text-brass">Two Culinary Hearts</span>
            </MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
            className="mt-6 max-w-xl text-cream/80 text-base sm:text-lg font-light leading-relaxed"
          >
            From handmade pasta to copper-handi curries, wood-fired pizzas to our in-house bakery —
            every plate is made with amore.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.6 }}
            className="mt-8 inline-flex items-center gap-3 rounded-full border border-cream/25 px-5 py-2.5 text-xs tracking-[0.2em] uppercase text-cream/85"
            data-testid="menu-alcohol-free-badge"
          >
            <Coffee size={14} strokeWidth={1.5} className="text-brass" />
            100% alcohol-free — coffee, chai, mocktails & shakes
          </motion.div>
        </div>
      </section>

      <div className="sticky top-[72px] z-[60] border-b border-sand bg-cream/90 backdrop-blur-md">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="flex gap-2 overflow-x-auto py-4 scrollbar-none" data-testid="menu-category-tabs">
            {MENU_CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                data-testid={`menu-tab-${c.id}`}
                className={`whitespace-nowrap rounded-full px-5 py-2.5 text-[12px] tracking-[0.14em] uppercase transition-all duration-300 ${
                  active === c.id
                    ? "bg-wine text-cream shadow-[0_10px_25px_-10px_rgba(114,47,55,0.5)]"
                    : "border border-sand text-faded hover:border-wine/40 hover:text-wine"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.5, ease: EASE }}
              data-testid={`menu-category-${category.id}`}
            >
              <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                {category.label.split("&")[0]}
                {category.label.includes("&") && (
                  <span className="italic text-wine">&{category.label.split("&")[1]}</span>
                )}
              </h2>
              <p className="mt-4 max-w-2xl text-faded text-sm sm:text-base font-light leading-relaxed">
                {category.tagline}
              </p>

              <div className="mt-12 columns-1 md:columns-2 xl:columns-3 gap-10">
                {category.groups.map((group) => (
                  <div key={group.title} className="mb-12 break-inside-avoid" data-testid={`menu-group-${group.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="flex items-baseline gap-4">
                      <h3 className="font-serif text-2xl font-medium tracking-tight text-wine">{group.title}</h3>
                      <span className="flex-1 border-b border-dotted border-ink/20" />
                    </div>
                    {group.note && (
                      <p className="mt-2 text-[12px] italic text-brass tracking-wide">{group.note}</p>
                    )}
                    <ul className="mt-5 space-y-3.5">
                      {group.items.map((item) => (
                        <li key={item.name} className="flex items-start gap-3">
                          <VegMark nv={item.nv} />
                          <span className="text-[15px] font-light leading-snug">{item.name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex flex-wrap items-center gap-6 border-t border-sand pt-8 text-xs text-faded tracking-wide" data-testid="menu-legend">
            <span className="flex items-center gap-2">
              <VegMark /> Vegetarian
            </span>
            <span className="flex items-center gap-2">
              <VegMark nv /> Non-vegetarian / Egg
            </span>
            <span className="italic">Dishes are subject to seasonal availability.</span>
          </div>
        </div>
      </section>

      <section className="bg-shell border-t border-sand py-20 sm:py-24">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <Overline className="text-wine">Tempted?</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight mt-5">
              Taste it <span className="italic text-wine">Tonight</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              to="/reservations"
              data-testid="menu-reserve-cta"
              className="mt-10 inline-block rounded-full bg-wine px-12 py-5 text-sm tracking-[0.16em] uppercase text-cream hover:bg-winedark hover:scale-[1.04] active:scale-95 transition-all duration-300"
            >
              Reserve a Table
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
